import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'lib/classNames';
import { CartContext } from 'components/cart';
import styled, { createGlobalStyle } from 'styled-components';
import Api from '../Api';

const CustomSelect = styled.select`
	border-color: ${(props) => props.secondary}!important;
	background: ${(props) => props.secondary};
	&:focus {
		background: ${(props) => props.background};
	}
`;

const BundleVariantsPicker = ({ CustomScroll, product, items, parentRef, onConfirm, buttonText }) => {
	const [stateItems, setStateItems] = useState([]);
	const [loading, setLoading] = useState(false);
	const { id: product_id, options, variants, images } = product;
	const {
		cart_settings: {
			theme: { background, secondary },
			modules: { checkout_button },
			rtl,
			money_format,
		},
		shop_url,
		shopify_cart_items,
		Utils,
		setBundleVariantsPickerShow,
		setCartItems,
	} = useContext(CartContext);

	const getSelectedOption = ({ name, options_with_values }) => {
		return options_with_values.find((option) => name === option.name).value;
	};
	const getVariantTitle = (options_with_values) => options_with_values.map(({ value }) => value).join(' / ');
	const findVariant = (options_with_values) =>
		variants.find(({ title }) => title === getVariantTitle(options_with_values));
	const setOption = ({ name, id, value }) => {
		const new_state = [...stateItems];
		const updated_item = new_state.find((item) => item.id === id);
		const index = new_state.indexOf(updated_item);
		new_state[index].options_with_values = new_state[index].options_with_values.map((option) => {
			if (option.name !== name) return option;
			return { name, value: value };
		});
		setStateItems(new_state);
	};

	const cartHasItem = (variant_id) => !!shopify_cart_items.find((item) => item.variant_id === variant_id);

	const pushChanges = () => {
		setLoading(true);
		//group same items with the same variant
		const new_cart_items = stateItems.reduce((acc, cur) => {
			const { options_with_values } = cur;
			const variant_title = getVariantTitle(options_with_values);
			const selected_variant = findVariant(options_with_values);
			if (acc[variant_title]) {
				acc[variant_title].quantity += 1;
			} else {
				acc[variant_title] = {
					id: selected_variant.id,
					quantity: 1,
				};
			}
			return acc;
		}, {});

		//generate updates object
		const updates = Object.values(new_cart_items).reduce((acc, cur) => {
			const { id, quantity } = cur;
			acc[id] = quantity;
			return acc;
		}, {});

		//find removed items
		const cart_existing_variants = shopify_cart_items
			.filter((item) => item.product_id === product_id)
			.map(({ variant_id }) => variant_id);
		const updates_variants = Object.keys(updates);
		const removed_variants = cart_existing_variants.reduce((acc, cur) => {
			if (!updates_variants.includes(cur.toString())) acc[cur] = 0;
			return acc;
		}, {});

		//update items
		Api.post(`https://${shop_url}/cart/update.js`, {
			updates: { ...updates, ...removed_variants },
		})
			.then(({ data }) => {
				setCartItems(data.items);
				setLoading(false);
				setBundleVariantsPickerShow(false);
			})
			.catch((error) => {
				console.log(error);
				setLoading(false);
			});
	};

	useEffect(() => {
		const new_state = items
			.map((item) => {
				return new Array(item.quantity).fill().map((_, i) => ({
					...item,
					id: `${item.id}_${i}`,
					quantity: 1,
				}));
			})
			.flat(1);
		setStateItems(new_state);
	}, [items]);

	return (
		<div
			className={classNames('px-5 py-3 flex flex-col max-h-full')}
			style={{
				background: Utils.formatColor(background),
				paddingBottom: '60px',
			}}
		>
			<div className={classNames('flex justify-end pb-3')}>
				<svg
					aria-hidden="true"
					focusable="false"
					data-icon="times"
					role="img"
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 320 512"
					className={classNames('w-3 h-3 cursor-pointer')}
					onClick={() => setBundleVariantsPickerShow(false)}
				>
					<path
						fill="currentColor"
						d="M193.94 256L296.5 153.44l21.15-21.15c3.12-3.12 3.12-8.19 0-11.31l-22.63-22.63c-3.12-3.12-8.19-3.12-11.31 0L160 222.06 36.29 98.34c-3.12-3.12-8.19-3.12-11.31 0L2.34 120.97c-3.12 3.12-3.12 8.19 0 11.31L126.06 256 2.34 379.71c-3.12 3.12-3.12 8.19 0 11.31l22.63 22.63c3.12 3.12 8.19 3.12 11.31 0L160 289.94 262.56 392.5l21.15 21.15c3.12 3.12 8.19 3.12 11.31 0l22.63-22.63c3.12-3.12 3.12-8.19 0-11.31L193.94 256z"
						class=""
					></path>
				</svg>
			</div>
			<CustomScroll
				className={classNames('flex-1')}
				thumbBg={Utils.formatColor(secondary)}
				trackBg={Utils.formatColor(background)}
				isMobile={Utils.isMobile()}
			>
				{stateItems.map((item, i) => {
					const { id, options_with_values } = item;
					const selected_variant = findVariant(options_with_values);
					return (
						<div className={classNames('m-2 mb-4 relative')}>
							<span
								className={classNames(
									'w-4 h-4 absolute top-0 rounded-full flex-center font-bold transform -translate-x-2 -translate-y-2',
									rtl ? 'right-0' : 'left-0'
								)}
								style={{
									width: 20,
									height: 20,
									backgroundColor: Utils.formatColor(checkout_button.background),
									color: Utils.formatColor(checkout_button.button_text.color),
								}}
							>
								{i + 1}
							</span>
							<VariantData {...{ item, selected_variant, images }} key={id} />
							<div className={classNames('flex -mx-2 flex-wrap')}>
								{options.map((option) => (
									<Option
										option={option}
										key={option.id}
										value={getSelectedOption({
											name: option.name,
											options_with_values,
										})}
										onChange={({ name, value }) => setOption({ name, id, value })}
									/>
								))}
							</div>
						</div>
					);
				})}
			</CustomScroll>
			<button
				className={classNames('custom-btn w-full rounded w-full p-4 my-3', {
					// 'opacity-50 cursor-not-allowed': !selectedVariant || selectedVariant.inventory_quantity === 0,
				})}
				style={{
					backgroundColor: Utils.formatColor(checkout_button.background),
					borderColor: Utils.formatColor(checkout_button.background),
					color: Utils.formatColor(checkout_button.button_text.color),
				}}
				onClick={() => pushChanges()}
				disabled={false}
			>
				confirm
			</button>
		</div>
	);
};

const Option = ({ option, value, onChange }) => {
	const {
		cart_settings: {
			theme: { background, secondary },
		},
		Utils,
	} = useContext(CartContext);
	return (
		<div className={classNames('px-2 w-1/2')}>
			<label className={classNames('block capitalize text-sm font-bold mb-1')}>{option.name}</label>
			<div className={classNames('relative mb-2')}>
				<CustomSelect
					background={Utils.formatColor(background)}
					secondary={Utils.formatColor(secondary)}
					className={classNames(
						'block appearance-none w-full m-0 border-2 py-3 px-4 pr-8 rounded leading-tight focus:outline-none h-auto'
					)}
					value={value}
					onChange={({ target }) => {
						onChange({ name: option.name, value: target.value });
					}}
				>
					{option.values.map((value, key) => {
						return <option value={value}>{value}</option>;
					})}
				</CustomSelect>
				<div className={classNames('pointer-events-none absolute inset-y-0 right-0 flex items-center px-2')}>
					<svg
						className={classNames('fill-current h-4 w-4')}
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 20 20"
					>
						<path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
					</svg>
				</div>
			</div>
		</div>
	);
};

const VariantData = ({ item, children, selected_variant, images }) => {
	const {
		cart_settings: { rtl, money_format },
		Utils,
	} = useContext(CartContext);
	const { product_title, price } = item;
	const image = selected_variant.image_id
		? images.find(({ id }) => id === selected_variant.image_id).src
		: item.image;
	return (
		<div className={classNames('flex')}>
			<div
				className={classNames('bg-cover bg-center w-20 h-20 rounded', rtl ? 'ml-2' : 'mr-2')}
				style={{
					backgroundImage: `url(${image})`,
				}}
			/>
			<div className={classNames('flex-1 flex flex-col')}>
				<b
					className={classNames('block font-bold mb-2 overflow-hidden')}
					style={{
						fontSize: Utils.em(1),
						lineHeight: Utils.em(1 + 0.2),
						maxHeight: Utils.em(2 + 0.4),
					}}
				>
					{product_title}
				</b>
				<p className={classNames('flex-1')}>{selected_variant.title}</p>
				<p
					className={classNames('font-bold', rtl ? 'text-left' : 'text-right')}
					dangerouslySetInnerHTML={{
						__html: Utils.formatMoney(price),
					}}
				/>
			</div>
		</div>
	);
};

BundleVariantsPicker.propTypes = {};

export default BundleVariantsPicker;
